import { useRef, type ReactElement, type ComponentProps, useState } from 'react';
import { useSearchParams } from 'react-router';
import { TdsIcon } from '@scania/tegel-react';
import { clsx } from 'clsx';
import { type UpdateQueryParameterValues, type FilterKey } from '../../../hooks/use-update-query-parameter-values';
import styles from './style.module.scss';
import useTotal from './hooks/use-total';
import useRenderPieChart from './hooks/use-render-pie-chart';
import useSyncSelectedChartSegment from './hooks/use-sync-selected-segment';
import { type PieChartItem } from '@/utils/draw-pie-chart';
import { type SelectedChartSegment } from '@/app/config/chart-segments';

type TdsIconProperties = ComponentProps<typeof TdsIcon>;

interface ChartProperties {
  readonly header: string;
  readonly items: PieChartItem[];
  readonly iconName: TdsIconProperties['name'];
  readonly selectedSegment: SelectedChartSegment | null;
  readonly indexKey: FilterKey;
  readonly updateQueryParameterValues: UpdateQueryParameterValues;
}

const Chart = ({
  header,
  items,
  iconName,
  selectedSegment,
  indexKey,
  updateQueryParameterValues,
}: ChartProperties): ReactElement => {
  const chartRef = useRef<HTMLDivElement>(null);
  const total = useTotal(items);

  const [selectedChartItem, setSelectedChartItem] = useState<PieChartItem | null>(null);
  const [searchParameters] = useSearchParams();

  useRenderPieChart({
    chartRef,
    items,
    selectedSegment,
    indexKey,
    searchParameters,
    updateQueryParameterValues,
    onSegmentClick: setSelectedChartItem,
  });

  useSyncSelectedChartSegment({
    selectedChartItem,
    indexKey,
    searchParameters,
    updateQueryParameterValues,
    resetSelectedChartItem: () => setSelectedChartItem(null),
  });

  return (
    <div className={styles.container}>
      <h3 className={styles.header}>{header}</h3>
      <div className={styles.content}>
        <div className={styles.chart}>
          <div ref={chartRef} />
          {total > 0 && <TdsIcon className={styles.icon} name={iconName} size="24px" />}
        </div>
        <ul className={styles.legend}>
          {items.map((item) => (
            <li key={item.header}>
              <button
                type="button"
                className={clsx({
                  [styles.active]: selectedSegment?.segmentIndex === item.segmentIndex,
                  [styles.faded]: selectedSegment && selectedSegment.segmentIndex !== item.segmentIndex,
                })}
                onClick={() => setSelectedChartItem(item)}
              >
                {/* eslint-disable-next-line react/forbid-dom-props -- to set dynamic background color */}
                <span className={styles.color} style={{ backgroundColor: item.color }} />
                <span className={styles.label}>{item.header}</span>
                <span className={styles.value}>{item.value}</span>
              </button>
            </li>
          ))}
          <li
            className={clsx(styles.total, {
              [styles.faded]: selectedSegment,
            })}
          >
            <span className={styles.label}>Total vehicles</span>
            <span className={styles.value}>{total}</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Chart;
